import React from "react";
import type { Project } from "../../../api/useProjects";
import { useQuantities } from "../../../api/useQuantities";
import { useSetups } from "../../../api/useSetups";
import { useProjectSource } from "../../../api/useSources";
import useDebounce from "@ui/lib/hooks/useDebounce";
import { isNonEmptyArray } from "diagnostics-lib/array";
import SidePanelLayout from "@ui/SidePanel/Layout";
import useSelectedAnalyses from "../Analysis/useSelectedAnalyses";
import FailureEvolution from "./FailureEvolution";
import DiagnosisSymptomAndImpacts from "./Symptoms";
import { useIndexedFailureValues } from "../../../api/useIndexedFailureValues";
import { useIndexedManualFailureValues } from "../../../api/useManualFailureValues";
import { styled } from "@linaria/react";
import BlankStateDiagnosis from "./FailureEvolution/components/BlankStateDiagnosis";
import { useIndexedFailureMetadata } from "../../../api/useFailureMetadata";
import { useOutletContext } from "react-router";
import { useSettings } from "../../../api/useSettings";
import useActiveAnalysesWithDisplayFilters from "../../../api/useActiveAnalysesWithDisplayFilters";

const SidePanelLayoutNoFlex = styled(SidePanelLayout)`
  flex: none;
`;
const ExplorationSidePanelLayout = styled(SidePanelLayout)`
  flex: none;
  padding: 24px 0 !important;
`;

export const ProjectDiagnosisNonIdealProxy = () => {
  const project = useOutletContext<Project>();
  if (!project) throw new Error("no project");
  const settings = useSettings();
  const LayoutComponent = settings.NEW_NAVIGATION_ENABLED
    ? ExplorationSidePanelLayout
    : SidePanelLayoutNoFlex;

  const source = useProjectSource(project);
  const { displayFilterRemovesAllActiveAnalyses, activeAnalyses } =
    useActiveAnalysesWithDisplayFilters(project.id);
  const quantities = useQuantities(project.id);
  const setups = useSetups(project.id);
  const indexedFailureMetadata = useIndexedFailureMetadata(project.id);
  const indexedFailureValues = useIndexedFailureValues(project.id);
  const indexedManualFailureValues = useIndexedManualFailureValues(project.id);

  const { selectedAnalyses } = useSelectedAnalyses(project.id, activeAnalyses);
  const debouncedSelectedAnalyses = useDebounce(selectedAnalyses, 300);

  if ((!isNonEmptyArray(activeAnalyses) && !displayFilterRemovesAllActiveAnalyses) || !source)
    return <BlankStateDiagnosis project={project} />;

  return (
    <LayoutComponent>
      <FailureEvolution
        project={project}
        source={source}
        quantities={quantities}
        setups={setups}
        activeAnalyses={activeAnalyses}
        indexedFailureMetadata={indexedFailureMetadata}
        indexedFailureValues={indexedFailureValues}
        indexedManualFailureValues={indexedManualFailureValues}
      />

      {debouncedSelectedAnalyses[0] && debouncedSelectedAnalyses.length === 1 ? (
        <DiagnosisSymptomAndImpacts
          visibleSymptomHistogramChartId={`${debouncedSelectedAnalyses[0].id}-symptom-histogram`}
          project={project}
          quantities={quantities}
          setups={setups}
          analysis={debouncedSelectedAnalyses[0]}
          indexedFailureMetadata={indexedFailureMetadata}
          indexedFailureValues={indexedFailureValues}
          indexedManualFailureValues={indexedManualFailureValues}
        />
      ) : null}
    </LayoutComponent>
  );
};
